import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import {
  BookOpenText,
  Briefcase,
  Coins,
  Compass,
  KeyRound,
  LineChart,
  LogOut,
  Send,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import AccountStrip from "@/components/AccountStrip";
import SymbolSearchModal from "@/components/SymbolSearchModal";

const RAIL_ITEMS = [
  { to: "/desk",             label: "Desk",      testid: "rail-desk",       Icon: Sparkles     },
  { to: "/discover",         label: "Discover",  testid: "rail-discover",   Icon: Compass      },
  { to: "/portfolios",       label: "Book",      testid: "rail-portfolios", Icon: Briefcase    },
  { to: "/analyze",          label: "Analyze",   testid: "rail-analyze",    Icon: LineChart    },
  { to: "/settings/alpaca",  label: "Alpaca",    testid: "rail-alpaca",     Icon: BookOpenText },
  { to: "/settings/capital", label: "Capital",   testid: "rail-capital",    Icon: ShieldCheck  },
  { to: "/settings/oanda",   label: "OANDA",     testid: "rail-oanda",      Icon: Coins        },
  { to: "/settings/telegram",label: "Telegram",  testid: "rail-telegram",   Icon: Send         },
];
const RAIL_ADMIN = { to: "/admin", label: "Admin", testid: "rail-admin", Icon: ShieldAlert };

export default function AppShell() {
  const { user, isMaster, logout } = useAuth();
  const { pathname } = useLocation();
  const isCloakScreen = pathname.startsWith("/admin/users/");
  const [searchOpen, setSearchOpen] = useState(false);

  // Global "/" shortcut opens the symbol search.
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === "/" &&
        !e.metaKey && !e.ctrlKey && !e.altKey &&
        !["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName) &&
        !e.target?.isContentEditable
      ) {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="aurion-grain relative min-h-screen">
      {isCloakScreen ? <CloakBanner /> : null}
      <SymbolSearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
      <div className="relative z-10 flex min-h-screen">
        {/* Main column */}
        <div className="flex min-w-0 flex-1 flex-col">
          <AccountStrip onOpenSearch={() => setSearchOpen(true)} />
          <main className="min-w-0 flex-1 px-4 py-3">
            <Outlet />
          </main>
        </div>
        {/* Right-docked icon rail */}
        <aside
          className="aurion-rail sticky top-0 flex h-screen shrink-0 flex-col justify-between"
          data-testid="app-rail"
        >
          <div className="flex flex-col items-stretch pt-2">
            {RAIL_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                data-testid={it.testid}
                className={({ isActive }) =>
                  `aurion-rail-item ${isActive ? "active" : ""}`
                }
                title={it.label}
              >
                <it.Icon className="h-4 w-4" />
                <span className="rail-label">{it.label}</span>
              </NavLink>
            ))}
            {isMaster ? (
              <NavLink
                to={RAIL_ADMIN.to}
                data-testid={RAIL_ADMIN.testid}
                className={({ isActive }) =>
                  `aurion-rail-item ${isActive ? "active" : ""}`
                }
                title={RAIL_ADMIN.label}
              >
                <RAIL_ADMIN.Icon className="h-4 w-4" />
                <span className="rail-label">{RAIL_ADMIN.label}</span>
              </NavLink>
            ) : null}
          </div>
          <div className="flex flex-col items-stretch pb-2">
            <NavLink
              to="/account/password"
              data-testid="rail-password"
              className={({ isActive }) =>
                `aurion-rail-item ${isActive ? "active" : ""}`
              }
              title="Password"
            >
              <KeyRound className="h-4 w-4" />
              <span className="rail-label">Pass</span>
            </NavLink>
            <div
              className="aurion-rail-item"
              data-testid="rail-user"
              title={user?.email || ""}
            >
              <UserRound className="h-4 w-4" />
              <span className="rail-label">
                {isMaster ? "MSTR" : "User"}
              </span>
            </div>
            <button
              type="button"
              data-testid="rail-logout"
              onClick={logout}
              className="aurion-rail-item hover:!text-[var(--aurion-red)]"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
              <span className="rail-label">Out</span>
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

function CloakBanner() {
  return (
    <div
      data-testid="cloak-banner"
      className="sticky top-0 z-20 flex items-center justify-center gap-2 border-b border-red-500/40 bg-red-500/15 py-2 text-xs font-semibold uppercase tracking-widest text-red-300"
    >
      <ShieldAlert className="h-4 w-4" />
      Cloak mode — read-only view of another user's data
    </div>
  );
}
